import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import CardSlider from './components/CardSlider';
import RespondDrawModal from './components/RespondDrawModal';
import SelectBattleDeckModal from './components/SelectBattleDeckModal';
let sliderState = {
  discussionId: null,
  battle: null,
  deckCards: [],
  playedCardIds: [],
  loaded: false,
  loading: false,
  lastPostCount: 0,
  selectedElementalId: null,
  selectedBonusId: null,
  drawModalShownForBattleId: null,
  deckModalShownForBattleId: null
};
async function refreshSliderState(discussionId) {
  const actor = app.session.user;
  if (!actor || sliderState.loading) return;

  // A user can have several concurrent active battles (one per opponent), so
  // this is a list — find the one, if any, that belongs to the discussion
  // currently being viewed rather than assuming there's only one.
  const activeBattles = actor.attribute('arenaActiveBattles') ?? [];
  const activeBattleInfo = activeBattles.find(b => b.discussionId === discussionId) ?? null;
  if (!activeBattleInfo) {
    sliderState = {
      ...sliderState,
      battle: null,
      loaded: true,
      discussionId
    };
    return;
  }
  sliderState.loading = true;
  try {
    const battles = await app.store.find('arena-battles');
    const battle = battles.find(b => b.discussionId() === discussionId && b.status() === 'active') ?? null;
    let deckCards = [];
    let playedCardIds = [];
    if (battle) {
      const isChallenger = battle.challengerId() === parseInt(actor.id());

      // Full card details for THIS side's locked-in battle deck —
      // GetBattleDeckCardsController resolves the same override-or-slot
      // source ProcessCardPlay validates plays against
      // (ArenaBattle::deckCardIdsFor()), already in the right order, and
      // bypasses CardResource's is_public/personal-grant visibility check
      // entirely. That check is correct for the general card list/deck
      // builder, but wrong here: a card locked into this specific battle
      // (a deck slot saved while its grant still stood, or — since
      // random_cards deliberately pools both players' personal grants
      // together, see RandomDeckBuilder's docblock — a random draw that
      // handed this player the OPPONENT's personal card) must stay
      // selectable regardless of whether the viewing actor personally holds
      // a grant for it right now. Resolving it against the general
      // CardResource list (the old approach here) silently dropped any such
      // card from the picker — locked into the deck, but impossible to
      // actually select.
      const deckCardsResponse = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/battles/${battle.id()}/deck-cards`
      });
      app.store.pushPayload(deckCardsResponse);
      deckCards = (deckCardsResponse.data ?? []).map(r => app.store.getById('arena-cards', r.id)).filter(c => c != null);
      const turnData = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/battles/${battle.id()}/played-cards`
      });
      playedCardIds = isChallenger ? turnData.challengerPlayedCardIds ?? [] : turnData.opponentPlayedCardIds ?? [];
    }
    sliderState = {
      ...sliderState,
      discussionId,
      battle,
      deckCards,
      playedCardIds,
      loaded: true,
      loading: false,
      selectedElementalId: null,
      selectedBonusId: null,
      // Reset the auto-open flag once the draw proposal is gone, so the modal
      // shows again next time one comes in.
      drawModalShownForBattleId: battle?.drawProposedById() != null ? sliderState.drawModalShownForBattleId : null,
      // Same idea: once this player has played a card (or there's no battle
      // left), the deck can no longer be changed, so the flag resets and the
      // modal is free to show again for whatever battle comes next.
      deckModalShownForBattleId: battle && playedCardIds.length === 0 ? sliderState.deckModalShownForBattleId : null
    };
  } catch {
    sliderState = {
      ...sliderState,
      loaded: true,
      loading: false
    };
  }
  m.redraw();
}
function handleSelectElemental(card) {
  const cardId = parseInt(card.id());
  sliderState.selectedElementalId = sliderState.selectedElementalId === cardId ? null : cardId;
  m.redraw();
}
function handleSelectBonus(card) {
  const cardId = parseInt(card.id());
  sliderState.selectedBonusId = sliderState.selectedBonusId === cardId ? null : cardId;
  m.redraw();
}

// Refreshes the post stream of the discussion currently being viewed so the
// new system post (turn/surrender/draw) appears immediately, the same way
// Flarum's own ReplyComposer does it. No route change, no full page reload.
//
// Our card-play post is created via a raw app.request(), so its response
// never gets pushed into the store and the discussion's postIds()/postCount
// stay stale — refetch the discussion first, then let the stream catch up.
//
// goToLast() is used instead of update(): update() only loads the new range
// if the stream is already scrolled to the end, so a user scrolled up would
// never see or get scrolled to the new post. goToLast() unconditionally
// loads the last page and scrolls to it.
function refreshPostStream(discussionId) {
  const stream = app.current.get('stream');
  if (discussionId !== null && stream && typeof stream.goToLast === 'function') {
    app.store.find('discussions', String(discussionId)).then(() => stream.goToLast()).then(() => m.redraw()).catch(() => {
      // If the refetch fails, fall back to the old soft re-navigation
      // rather than leaving the stream silently stale.
      const path = m.route.get();
      m.route.set('/');
      setTimeout(() => m.route.set(path), 50);
    });
  } else {
    // No active stream (shouldn't normally happen while a slider is shown):
    // soft re-navigation forces DiscussionPage to remount.
    const path = m.route.get();
    m.route.set('/');
    setTimeout(() => m.route.set(path), 50);
  }
  if (discussionId !== null) {
    refreshSliderState(discussionId);
  }
}
async function submitTurn() {
  const {
    selectedElementalId,
    selectedBonusId,
    discussionId
  } = sliderState;
  if (!selectedElementalId || !discussionId) return;
  let content = `[arena-card=${selectedElementalId}]`;
  if (selectedBonusId) content += `\n[arena-card=${selectedBonusId}]`;
  await app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/posts`,
    body: {
      data: {
        type: 'posts',
        attributes: {
          content
        },
        relationships: {
          discussion: {
            data: {
              type: 'discussions',
              id: String(discussionId)
            }
          }
        }
      }
    }
  });
  hideSlider();
  refreshPostStream(discussionId);
}
function hideSlider() {
  sliderState.battle = null;
  sliderState.loaded = true;
  sliderState.selectedElementalId = null;
  sliderState.selectedBonusId = null;
  m.redraw();
}

// Unlike surrender/draw (which keep the discussion, just lock it with a
// final post), a decline deletes the battle's discussion server-side — so
// there's nothing left to refetch/scroll to. Send the player back to the
// forum index instead of calling refreshPostStream(), which would try to
// re-fetch a discussion that no longer exists.
function handleDeclined() {
  hideSlider();
  sliderState = {
    ...sliderState,
    discussionId: null,
    battle: null,
    loaded: true
  };
  m.route.set(app.route('index'));
}
function buildSlider(battle, wrapClass) {
  const actor = app.session.user;
  const mana = battle.challengerId() === parseInt(actor.id()) ? battle.challengerMana() : battle.opponentMana();
  const available = sliderState.deckCards.filter(c => !sliderState.playedCardIds.includes(parseInt(c.id())));
  const bonusCards = available.filter(c => c.isBonus?.() ?? c.cardType?.() === 'bonus');
  const canSubmit = sliderState.selectedElementalId !== null && (bonusCards.length === 0 || sliderState.selectedBonusId !== null);
  return m("div", {
    className: wrapClass
  }, m(CardSlider, {
    cards: sliderState.deckCards,
    playedCardIds: sliderState.playedCardIds,
    mana: mana,
    battle: battle,
    selectedElementalId: sliderState.selectedElementalId,
    selectedBonusId: sliderState.selectedBonusId,
    onSelectElemental: handleSelectElemental,
    onSelectBonus: handleSelectBonus,
    onSubmitTurn: submitTurn,
    canSubmit: canSubmit,
    onSurrendered: () => refreshPostStream(sliderState.discussionId),
    onDrawProposed: () => refreshPostStream(sliderState.discussionId),
    onDeclined: handleDeclined
  }));
}

// Status bar shown while a draw proposal is pending: turn timer is paused and
// the normal card slider is hidden. Proposer sees "waiting for response";
// the other player sees "opponent proposed a draw" + a button to reopen the
// response modal.

let _cancelDrawLoading = false;
function cancelDraw(battle) {
  if (_cancelDrawLoading) return;
  _cancelDrawLoading = true;
  app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/arena/battles/${battle.id()}/cancel-draw`
  }).then(() => {
    refreshPostStream(sliderState.discussionId);
  }).catch(() => {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('forumaker-arena.forum.propose_draw.cancel_error'));
  }).finally(() => {
    _cancelDrawLoading = false;
  });
}
function buildDrawPendingBar(battle, wrapClass, isProposer) {
  const opponentId = battle.drawProposedById();
  const opponentUser = app.store.getById('users', String(opponentId));
  const opponentName = opponentUser?.displayName() ?? app.translator.trans('forumaker-arena.forum.respond_draw.player_fallback', {
    id: opponentId
  });
  return m("div", {
    className: wrapClass
  }, m("div", {
    className: "ArenaCardSlider ArenaCardSlider--drawPending"
  }, m("div", {
    className: "ArenaFinalPost ArenaFinalPost--draw ArenaCardSlider-drawPendingBar"
  }, m("div", {
    className: "ArenaFinalPost-icon"
  }, m("i", {
    className: "fas fa-handshake"
  })), m("div", {
    className: "ArenaFinalPost-body"
  }, m("div", {
    className: "ArenaFinalPost-title"
  }, isProposer ? app.translator.trans('forumaker-arena.forum.propose_draw.waiting_title') : app.translator.trans('forumaker-arena.forum.respond_draw.pending_title')), m("div", {
    className: "ArenaFinalPost-subtitle"
  }, m("i", {
    className: "fas fa-hourglass-half"
  }), isProposer ? app.translator.trans('forumaker-arena.forum.propose_draw.waiting_subtitle') : app.translator.trans('forumaker-arena.forum.respond_draw.pending_subtitle', {
    name: m("strong", null, opponentName)
  }))), !isProposer && m("button", {
    type: "button",
    className: "Button Button--primary ArenaCardSlider-drawPendingBtn",
    onclick: () => openRespondDrawModal(battle, opponentName)
  }, app.translator.trans('forumaker-arena.forum.respond_draw.respond_btn')), isProposer && m("button", {
    type: "button",
    className: "Button ArenaCardSlider-drawPendingBtn ArenaCardSlider-drawCancelBtn",
    onclick: () => cancelDraw(battle)
  }, app.translator.trans('forumaker-arena.forum.propose_draw.cancel_proposal_btn')))));
}
function openRespondDrawModal(battle, proposerName) {
  app.modal.show(RespondDrawModal, {
    battle,
    proposerName,
    onResponded: () => refreshPostStream(sliderState.discussionId)
  });
}

// Shown both by the auto-open trigger below (see the portal's view()) and by
// CardSlider's manual "change deck" button — a plain refresh is enough here,
// no post was created, just a battle row update, so refreshSliderState()
// alone (not refreshPostStream()) is all that's needed to pick up the new
// deck's cards.
function openSelectDeckModal(battle, currentDeckSlotId) {
  app.modal.show(SelectBattleDeckModal, {
    battle,
    currentDeckSlotId,
    onSelected: () => {
      if (sliderState.discussionId !== null) refreshSliderState(sliderState.discussionId);
    }
  });
}

// Portal: the slider is mounted via m.mount() directly into document.body,
// outside any Flarum layout. m.mount() participates in autoredraw and
// doesn't interfere with routing (unlike m.render() inside view()).

let _portalMounted = false;
function ensurePortalMounted() {
  if (_portalMounted) return;
  _portalMounted = true;
  const el = document.createElement('div');
  el.id = 'arena-slider-portal';
  // Outer container is full-width, transparent, no pointer events — same
  // pattern as Flarum's .App-composer. All visual styling and height
  // clamping happens on the inner .ArenaCardSlider-portalInner.
  Object.assign(el.style, {
    position: 'fixed',
    left: '0',
    right: '0',
    bottom: '10px',
    zIndex: '1020',
    pointerEvents: 'none',
    display: 'none'
  });
  document.body.appendChild(el);

  // Portal geometry is only recalculated inside m.mount()'s view(), called
  // on m.redraw() — without an explicit resize listener, resizing the
  // window left the slider stretched to the old width. rAF coalesces
  // frequent resize events into one redraw per frame.
  let resizeRaf = null;
  window.addEventListener('resize', () => {
    if (resizeRaf !== null) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = null;
      m.redraw();
    });
  });
  m.mount(el, {
    view: () => {
      const actor = app.session?.user;
      const inBattle = !!actor && sliderState.loaded && !!sliderState.battle;

      // Auto-open the deck-confirmation modal for the side that didn't get
      // to pick their deck explicitly — the opponent's deck slot is
      // auto-locked the moment the battle is created, so this fires
      // regardless of whose turn it currently is. Only ever targets the
      // opponent: the challenger already picked explicitly in ChallengeModal.
      if (inBattle) {
        const battle = sliderState.battle;
        const battleId = parseInt(battle.id());
        const isOpponent = battle.opponentId() === parseInt(actor.id());
        const hasPlayed = sliderState.playedCardIds.length > 0;

        // battle.opponentDeckConfirmed() is the persisted signal
        // (opponent_deck_confirmed_at) — deckModalShownForBattleId only
        // guards against re-opening the modal twice within the same page
        // view while waiting on the first refetch.
        if (isOpponent && !hasPlayed && !battle.opponentDeckConfirmed() && sliderState.deckModalShownForBattleId !== battleId) {
          sliderState.deckModalShownForBattleId = battleId;
          setTimeout(() => openSelectDeckModal(battle, battle.opponentDeckSlotId()), 0);
        }
      }
      const drawProposedById = inBattle ? sliderState.battle.drawProposedById() : null;
      const pendingDraw = inBattle && drawProposedById !== null;
      const isMyTurn = inBattle && sliderState.battle.nextPlayerId() === parseInt(actor.id());
      const shouldShow = inBattle && (isMyTurn || pendingDraw);

      // Body class hides the default Composer while a battle is active
      document.body.classList.toggle('ArenaInBattle', inBattle);
      if (!shouldShow) {
        el.style.display = 'none';
        return null;
      }
      if (pendingDraw) {
        const isProposer = drawProposedById === parseInt(actor.id());

        // Auto-open the response modal for the other player once per proposal.
        if (!isProposer && sliderState.drawModalShownForBattleId !== parseInt(sliderState.battle.id())) {
          sliderState.drawModalShownForBattleId = parseInt(sliderState.battle.id());
          const opponentUser = app.store.getById('users', String(drawProposedById));
          const opponentName = opponentUser?.displayName() ?? app.translator.trans('forumaker-arena.forum.respond_draw.player_fallback', {
            id: drawProposedById
          });
          setTimeout(() => openRespondDrawModal(sliderState.battle, opponentName), 0);
        }
      }

      // The "draw proposed" bar is a status banner, not a floating composer.
      // It used to render in the same fixed portal as the turn slider and
      // always stuck to the bottom of the screen, detached from the last
      // post. Now the portal is moved right after .PostStream so it lives in
      // normal document flow and scrolls with the page.
      if (pendingDraw) {
        const streamEl = document.querySelector('.DiscussionPage .PostStream');
        if (streamEl?.parentElement && el.previousElementSibling !== streamEl) {
          streamEl.insertAdjacentElement('afterend', el);
        }
        Object.assign(el.style, {
          position: 'static',
          left: '',
          right: '',
          bottom: '',
          zIndex: '5'
        });
        el.style.display = 'block';
        const isProposer = drawProposedById === parseInt(actor.id());
        return buildDrawPendingBar(sliderState.battle, 'ArenaCardSlider-portalInner ArenaCardSlider-portalInner--inline', isProposer);
      }

      // The normal turn slider stays a floating composer at the bottom of the screen.
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      Object.assign(el.style, {
        position: 'fixed',
        zIndex: '1020',
        bottom: '10px'
      });

      // Desktop (>=992px, .Page--cols active): align to the content column.
      // Mobile/tablet: full-width, since there's no column and .Page-content fills the screen.
      const contentCol = window.innerWidth >= 992 ? document.querySelector('.DiscussionPage .Page-content') : null;
      if (contentCol) {
        const rect = contentCol.getBoundingClientRect();
        el.style.left = Math.round(rect.left) + 'px';
        el.style.right = Math.round(window.innerWidth - rect.right) + 'px';
      } else {
        el.style.left = '0';
        el.style.right = '0';
      }
      el.style.display = 'block';
      return buildSlider(sliderState.battle, 'ArenaCardSlider-portalInner');
    }
  });
}
export default function addCardSlider() {
  // Reset state on navigating to a different discussion. The first oninit
  // also creates the portal — by this point app.session is already ready.
  extend(DiscussionPage.prototype, 'oninit', function () {
    ensurePortalMounted();
    sliderState = {
      discussionId: null,
      battle: null,
      deckCards: [],
      playedCardIds: [],
      loaded: false,
      loading: false,
      lastPostCount: 0,
      selectedElementalId: null,
      selectedBonusId: null,
      drawModalShownForBattleId: null,
      deckModalShownForBattleId: null
    };

    // The current user's arenaActiveBattles attribute is cached in the SPA
    // session and only refreshed on a full forum load, so it can go stale
    // if a battle/draw/surrender happened after the page loaded. Forcing a
    // refetch of the own profile on entering any discussion keeps it fresh.
    const actor = app.session.user;
    if (actor) {
      app.store.find('users', actor.id()).then(() => m.redraw()).catch(() => {});
    }
  });

  // The portal doesn't auto-unmount on leaving the discussion page — it lives
  // in document.body independently of DiscussionPage. Without this reset,
  // sliderState.battle stayed stale and the slider remained visible,
  // stretched full-width, on unrelated forum pages.
  extend(DiscussionPage.prototype, 'onremove', function () {
    sliderState = {
      discussionId: null,
      battle: null,
      deckCards: [],
      playedCardIds: [],
      loaded: false,
      loading: false,
      lastPostCount: 0,
      selectedElementalId: null,
      selectedBonusId: null,
      drawModalShownForBattleId: null,
      deckModalShownForBattleId: null
    };
    m.redraw();
  });

  // DiscussionPage.view only tracks turn changes to refresh data; the portal
  // itself is managed independently via m.mount() above.
  extend(DiscussionPage.prototype, 'view', function () {
    const actor = app.session.user;
    if (!actor) return;
    const discussion = this.discussion;
    if (!discussion) return;
    const discussionId = parseInt(discussion.id());
    // Same "match against the viewed discussion" logic as refreshSliderState:
    // the user may have another active battle open in a different discussion
    // at the same time, which must not affect this one.
    const activeBattles = actor.attribute('arenaActiveBattles') ?? [];
    const activeBattleInfo = activeBattles.find(b => b.discussionId === discussionId) ?? null;
    if (!activeBattleInfo) return;

    // Navigating to a DIFFERENT discussion than whatever sliderState was
    // last loaded for must force a fresh fetch, regardless of the
    // loaded/post-count checks below. Those only detect a NEW POST arriving
    // within the SAME discussion — they say nothing about having switched
    // discussions. oninit (above) is supposed to reset sliderState on every
    // navigation, but Mithril doesn't always remount DiscussionPage between
    // two discussions (a known Flarum gotcha — same component, different
    // route param, no guaranteed unmount), so oninit can't be relied on
    // alone. Concretely: this is what let one rematch's slider show the
    // PREVIOUS battle's deck under the new discussion, because the new
    // thread's comment count happened to equal the old sliderState's
    // lastPostCount (both fresh 1-post battle threads) and skipped the
    // reload below entirely.
    if (sliderState.discussionId !== discussionId) {
      sliderState.loaded = false;
      sliderState.lastPostCount = 0;
    }

    // Uses commentCount (the real Discussion attribute) plus
    // postIds().length as a second signal: commentCount only changes when
    // the discussion resource is re-serialized by the server, but a
    // realtime extension pushing the opponent's turn may only append the
    // new post id to postIds() without patching commentCount. Taking the
    // max of both catches a new post either way.
    const currentPostCount = Math.max(discussion.attribute('commentCount') ?? 0, discussion.postIds?.()?.length ?? 0);
    if (sliderState.loaded && sliderState.lastPostCount > 0 && currentPostCount !== sliderState.lastPostCount) {
      sliderState.loaded = false;
      sliderState.selectedElementalId = null;
      sliderState.selectedBonusId = null;
    }
    sliderState.lastPostCount = currentPostCount;
    if (!sliderState.loaded && !sliderState.loading) {
      refreshSliderState(discussionId);
    }
  });
}